import { supabaseClient } from '../supabase';
import { logger } from '../logger';
import type { AnalyticsEvent, ContentChange } from './types';
import { HOTEL_ID } from '../supabase/configClient';
import { sessionTracker } from './sessionTracker';

class AnalyticsService {
  private static instance: AnalyticsService;
  private queue: AnalyticsEvent[] = [];
  private isProcessing = false;
  private batchSize = 10;
  private batchTimeout = 5000;
  private timer: NodeJS.Timeout | null = null;
  private isEnabled = true;
  private offlineQueue: AnalyticsEvent[] = [];
  private isOnline = navigator.onLine;
  private retryCount = 0;
  private maxRetries = 3;

  private constructor() {
    this.startProcessing();
    sessionTracker.setAnalyticsService(this);
    this.setupNetworkListeners();
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      logger.debug('Network connection restored, processing offline queue', {
        category: 'Analytics',
        data: { offlineQueueSize: this.offlineQueue.length }
      });
      this.processOfflineQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      logger.debug('Network connection lost, events will be queued', {
        category: 'Analytics'
      });
    });
  }

  private async processOfflineQueue(): Promise<void> {
    if (this.offlineQueue.length === 0) return;

    logger.debug('Processing offline queue', {
      category: 'Analytics',
      data: { queueSize: this.offlineQueue.length }
    });

    // Move events from offline queue to main queue
    this.queue.unshift(...this.offlineQueue);
    this.offlineQueue = [];

    // Process the queue immediately
    this.processQueue();
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) return;

    // If offline, move events to offline queue
    if (!this.isOnline) {
      logger.debug('Device is offline, moving events to offline queue', {
        category: 'Analytics',
        data: { queueSize: this.queue.length }
      });
      this.offlineQueue.push(...this.queue);
      this.queue = [];
      return;
    }

    this.isProcessing = true;
    const batch = this.queue.splice(0, this.batchSize);

    try {
      // Log detailed batch content
      logger.debug('Processing analytics batch', {
        category: 'Analytics',
        data: {
          batchSize: batch.length,
          events: batch.map(event => ({
            type: event.event_type,
            name: event.event_name,
            data: {
              ...event.event_data,
              section_id: event.event_data?.section_id || 'unknown',
              section_name: event.event_data?.section_name || 'unknown',
              section_type: event.event_data?.section_type || 'unknown',
              content_id: event.event_data?.content_id || event.event_data?.item_id || 'unknown',
              content_type: event.event_data?.content_type || event.event_data?.item_type || 'unknown',
              source: event.event_data?.source || 'unknown'
            },
            timestamp: event.created_at
          }))
        }
      });

      // Add UUID for each event
      const batchWithIds = batch.map(event => ({
        ...event,
        id: crypto.randomUUID() // Generate UUID for each event
      }));

      const { error } = await supabaseClient
        .from('analytics_events')
        .insert(batchWithIds);

      if (error) {
        throw error;
      }
      
      // Reset retry count on success
      this.retryCount = 0;

      logger.debug('Analytics batch processed successfully', {
        category: 'Analytics',
        data: { 
          batchSize: batch.length,
          firstEvent: {
            type: batch[0]?.event_type,
            name: batch[0]?.event_name,
            timestamp: batch[0]?.created_at
          },
          lastEvent: {
            type: batch[batch.length - 1]?.event_type,
            name: batch[batch.length - 1]?.event_name,
            timestamp: batch[batch.length - 1]?.created_at
          }
        }
      });
    } catch (error) {
      logger.error('Error processing analytics batch', {
        category: 'Analytics',
        data: { 
          error,
          batchSize: batch.length,
          events: batch.map(event => ({
            type: event.event_type,
            name: event.event_name,
            section_id: event.event_data?.section_id
          }))
        }
      });
      
      // Retry logic
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        logger.debug('Retrying batch processing', {
          category: 'Analytics',
          data: { 
            retryCount: this.retryCount,
            maxRetries: this.maxRetries,
            delay: Math.pow(2, this.retryCount - 1) * 1000
          }
        });
        
        // Put events back in the queue with exponential backoff
        setTimeout(() => {
          this.queue.unshift(...batch);
          this.isProcessing = false;
          this.processQueue();
        }, Math.pow(2, this.retryCount - 1) * 1000);
        
        return;
      } else {
        // After max retries, store in offline queue for later
        logger.warn('Max retries reached, moving to offline queue', {
          category: 'Analytics',
          data: { batchSize: batch.length }
        });
        this.offlineQueue.push(...batch);
        this.retryCount = 0;
      }
    } finally {
      this.isProcessing = false;
    }
  }

  private startProcessing(): void {
    this.timer = setInterval(() => {
      if (this.queue.length > 0) {
        logger.debug('Starting batch processing', {
          category: 'Analytics',
          data: {
            queueSize: this.queue.length,
            batchSize: Math.min(this.queue.length, this.batchSize),
            oldestEvent: {
              type: this.queue[0]?.event_type,
              name: this.queue[0]?.event_name,
              timestamp: this.queue[0]?.created_at
            }
          }
        });
        this.processQueue();
      }
    }, this.batchTimeout);
  }

  public trackEvent(event: Omit<AnalyticsEvent, 'created_at' | 'hotel_id' | 'session_id' | 'anonymous_id'>): void {
    // Skip if analytics is disabled
    if (!this.isEnabled) {
      return;
    }
    
    // Ensure event_data exists
    if (!event.event_data) {
      event.event_data = {};
    }
    
    // Format the event data according to the agreed structure
    const formattedEventData = {
      // Section information
      section_id: event.event_data.section_id,
      section_name: event.event_data.section_name,
      section_type: event.event_data.section_type,
      
      // Content information
      content_id: event.event_data.content_id || event.event_data.item_id,
      content_instance_id: event.event_data.content_instance_id || event.event_data.event_id,
      content_type: event.event_data.content_type || event.event_data.item_type,
      content_name: event.event_data.content_name || event.event_data.item_name,
      
      // Date and time information for events
      date: event.event_data.date,
      time: event.event_data.time || event.event_data.hour,
      
      // Category and type
      category: event.event_data.category,
      type: event.event_data.type,
      
      // Action information
      action: event.event_data.action,
      source_view: event.event_data.source_view || event.event_data.source,
      target_url: event.event_data.target_url || event.event_data.url,
      
      // Include any other data that was passed
      ...event.event_data,
      
      // Ensure timestamp is present
      timestamp: event.event_data.timestamp || new Date().toISOString()
    };
    
    // Handle special cases for section_id
    if (!formattedEventData.section_id) {
      // Check if we're in a special section based on event data or pathname
      const path = window.location.pathname;
      
      if (path.includes('/favoris') || 
          event.event_name?.includes('favorite') || 
          event.event_type === 'favorite') {
        formattedEventData.section_id = 'favorites';
        // Add section_name and section_type for favorites
        formattedEventData.section_name = 'Favorites';
        formattedEventData.section_type = 'favorites';
      } else if (path.includes('/share') || 
                event.event_name?.includes('share') || 
                event.event_type === 'share') {
        formattedEventData.section_id = 'share';
        // Add section_name and section_type for share
        formattedEventData.section_name = 'Share';
        formattedEventData.section_type = 'share';
      }
      
      // Log warning only if still missing after checks
      if (!formattedEventData.section_id) {
        logger.warn('Missing section_id in analytics event', {
          category: 'Analytics',
          data: {
            event_type: event.event_type,
            event_name: event.event_name,
            path: window.location.pathname
          }
        });
      }
    }

    // Ensure section_name and section_type are always present
    if (formattedEventData.section_id && !formattedEventData.section_name) {
      formattedEventData.section_name = this.getSectionNameFromId(formattedEventData.section_id);
    }

    if (formattedEventData.section_id && !formattedEventData.section_type) {
      formattedEventData.section_type = this.getSectionTypeFromId(formattedEventData.section_id);
    }

    const enrichedEvent: AnalyticsEvent = {
      ...event,
      hotel_id: HOTEL_ID,
      session_id: sessionTracker.getSessionId(),
      anonymous_id: sessionTracker.getAnonymousId(),
      created_at: new Date().toISOString(),
      page_path: window.location.pathname,
      event_data: formattedEventData
    };

    // Log detailed event
    logger.debug('Event added to analytics queue', {
      category: 'Analytics',
      data: {
        eventType: event.event_type,
        eventName: event.event_name,
        eventData: {
          ...formattedEventData,
          section_id: formattedEventData.section_id || 'unknown',
          section_name: formattedEventData.section_name || 'unknown',
          section_type: formattedEventData.section_type || 'unknown',
          content_id: formattedEventData.content_id || 'unknown',
          content_type: formattedEventData.content_type || 'unknown',
          source: formattedEventData.source_view || 'unknown'
        },
        queueSize: this.queue.length + 1,
        timestamp: enrichedEvent.created_at
      }
    });

    this.queue.push(enrichedEvent);

    // Process immediately if queue reaches batch size
    if (this.queue.length >= this.batchSize) {
      logger.debug('Queue reached batch size, processing immediately', {
        category: 'Analytics',
        data: {
          queueSize: this.queue.length,
          batchSize: this.batchSize,
          timestamp: new Date().toISOString()
        }
      });
      this.processQueue();
    }
  }

  public trackDetailCardView(
    sectionId: string,
    itemId: string,
    itemType: string,
    duration?: number,
    additionalData?: Record<string, any>
  ): void {
    // Get section_name and section_type from additionalData or set defaults
    const sectionName = additionalData?.section_name || this.getSectionNameFromId(sectionId);
    const sectionType = additionalData?.section_type || this.getSectionTypeFromId(sectionId);
    
    this.trackEvent({
      event_type: 'content_view',
      event_name: 'detail_card_view',
      event_data: {
        section_id: sectionId,
        section_name: sectionName,
        section_type: sectionType,
        
        content_id: itemId,
        content_type: itemType,
        content_name: additionalData?.item_name || 'Unknown',
        
        category: additionalData?.category || 'Unknown',
        type: additionalData?.type || 'Unknown',
        
        duration: duration || 0,
        source_view: 'detail_card',
        
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    });
  }

  public trackSectionView(
    sectionId: string,
    sectionType: string,
    viewType: 'list' | 'map',
    additionalData?: Record<string, any>
  ): void {
    // Get section_name from additionalData or set default
    const sectionName = additionalData?.section_name || this.getSectionNameFromId(sectionId);
    
    this.trackEvent({
      event_type: 'content_view',
      event_name: 'section_view',
      event_data: {
        section_id: sectionId,
        section_name: sectionName,
        section_type: sectionType,
        
        view_type: viewType,
        source_view: 'section',
        
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    });
  }

  public trackItemAction(
    sectionId: string,
    itemId: string,
    action: string,
    itemType: string,
    additionalData?: Record<string, any>
  ): void {
    // Get section_name and section_type from additionalData or set defaults
    const sectionName = additionalData?.section_name || this.getSectionNameFromId(sectionId);
    const sectionType = additionalData?.section_type || this.getSectionTypeFromId(sectionId);
    
    this.trackEvent({
      event_type: 'item_action',
      event_name: action,
      event_data: {
        section_id: sectionId,
        section_name: sectionName,
        section_type: sectionType,
        
        content_id: itemId,
        content_type: itemType,
        content_name: additionalData?.item_name || 'Unknown',
        
        category: additionalData?.category || 'Unknown',
        type: additionalData?.type || 'Unknown',
        
        action: action,
        source_view: additionalData?.source || 'unknown',
        target_url: additionalData?.url,
        
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    });
  }

  public async trackContentChange(change: Omit<ContentChange, 'created_at' | 'hotel_id'>): Promise<void> {
    try {
      logger.debug('Tracking content change', {
        category: 'Analytics',
        data: {
          table: change.table_name,
          action: change.action,
          recordId: change.record_id,
          changes: {
            old: change.old_data,
            new: change.new_data
          },
          timestamp: new Date().toISOString()
        }
      });

      const { error } = await supabaseClient
        .from('content_changes')
        .insert({
          ...change,
          hotel_id: HOTEL_ID,
          created_at: new Date().toISOString()
        });

      if (error) throw error;

      logger.debug('Content change tracked successfully', {
        category: 'Analytics',
        data: { 
          table: change.table_name,
          action: change.action,
          recordId: change.record_id,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      logger.error('Error tracking content change', {
        category: 'Analytics',
        data: { error, change }
      });
    }
  }

  // Helper method to get section name from ID
  private getSectionNameFromId(sectionId: string): string {
    // Handle special sections
    if (sectionId === 'favorites') return 'Favorites';
    if (sectionId === 'share') return 'Share';
    
    // For other sections, we could implement a lookup from navigation data
    // For now, return a default value
    return 'Unknown Section';
  }
  
  // Helper method to get section type from ID
  private getSectionTypeFromId(sectionId: string): string {
    // Handle special sections
    if (sectionId === 'favorites') return 'favorites';
    if (sectionId === 'share') return 'share';
    
    // For other sections, we could implement a lookup from navigation data
    // For now, return a default value
    return 'unknown';
  }

  public async getHotelStats(
    startDate: Date,
    endDate: Date
  ): Promise<any> {
    try {
      const { data, error } = await supabaseClient
        .rpc('get_hotel_stats', {
          p_hotel_id: HOTEL_ID,
          p_start_date: startDate.toISOString(),
          p_end_date: endDate.toISOString()
        });

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error fetching hotel stats', {
        category: 'Analytics',
        data: { error, hotelId: HOTEL_ID }
      });
      throw error;
    }
  }

  public enableAnalytics(): void {
    this.isEnabled = true;
    logger.debug('Analytics enabled', {
      category: 'Analytics'
    });
  }

  public disableAnalytics(): void {
    this.isEnabled = false;
    logger.debug('Analytics disabled', {
      category: 'Analytics'
    });
  }

  public isAnalyticsEnabled(): boolean {
    return this.isEnabled;
  }

  public getQueueStats(): {
    mainQueueSize: number;
    offlineQueueSize: number;
    isProcessing: boolean;
    isOnline: boolean;
  } {
    return {
      mainQueueSize: this.queue.length,
      offlineQueueSize: this.offlineQueue.length,
      isProcessing: this.isProcessing,
      isOnline: this.isOnline
    };
  }

  public destroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}

export const analyticsService = AnalyticsService.getInstance();